import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../utils/adminApi';
import Warranty from './Warranty';
import TaxForm from './TaxForm';

// Shop details for receipts — edit here to change what prints
const SHOP = {
  name: 'Ashop Rotterdam',
  address: 'Rotterdam, Netherlands',
  phone: '+31 6 14359710',
  kvk: '', // optional
  btw: '', // optional VAT number
};

// Translations for receipts (English / Dutch)
const T = {
  en: {
    receipt: 'RECEIPT', item: 'Item', qty: 'Qty', price: 'Price',
    subtotal: 'Subtotal', total: 'TOTAL', payment: 'Payment',
    cash: 'Cash', card: 'Card', date: 'Date', order: 'Order #',
    thanks: 'Thank you for shopping with us!', comeAgain: 'Have a great day.',
    print: 'Print Receipt', close: 'Close',
  },
  nl: {
    receipt: 'BON', item: 'Artikel', qty: 'Aant', price: 'Prijs',
    subtotal: 'Subtotaal', total: 'TOTAAL', payment: 'Betaling',
    cash: 'Contant', card: 'Pin', date: 'Datum', order: 'Bestelnr',
    thanks: 'Bedankt voor uw bezoek!', comeAgain: 'Fijne dag verder.',
    print: 'Bon Printen', close: 'Sluiten',
  },
};

// Default quick sale items — one-tap adds. Editable in the UI.
const DEFAULT_QUICK_ITEMS = [
  { id: 'q_print_bw', name: 'Print A4 B&W', price: 1.0, icon: 'fa-print', color: 'bg-blue-500' },
  { id: 'q_print_color', name: 'Print A4 Color', price: 1.5, icon: 'fa-palette', color: 'bg-pink-500' },
  { id: 'q_photocopy', name: 'Photocopy', price: 1.0, icon: 'fa-copy', color: 'bg-cyan-500' },
  { id: 'q_lamination', name: 'Lamination A4', price: 2.5, icon: 'fa-shield', color: 'bg-emerald-500' },
  { id: 'q_scan', name: 'Scan', price: 0.5, icon: 'fa-scanner', color: 'bg-indigo-500' },
];

const Register = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]); // [{product_id?, name, price, qty}]
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  // Keypad state (typing an amount)
  const [amount, setAmount] = useState('');
  const [customName, setCustomName] = useState('');

  // Receipt modal
  const [receiptSale, setReceiptSale] = useState(null); // { sale_number, subtotal, items, payment_method, date }
  const [receiptLang, setReceiptLang] = useState('en');

  // Lock state — locks the iPad view so cashier can't leave the register
  const [isLocked, setIsLocked] = useState(() => localStorage.getItem('register_locked') === 'true');
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockPin, setUnlockPin] = useState('');

  // Tab state — "register" (POS) or "history" (last 24h sales)
  const [activeTab, setActiveTab] = useState('register');
  const [salesHistory, setSalesHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Quick items — saved to localStorage, editable
  const [quickItems, setQuickItems] = useState(() => {
    try {
      const saved = localStorage.getItem('quick_items');
      return saved ? JSON.parse(saved) : DEFAULT_QUICK_ITEMS;
    } catch { return DEFAULT_QUICK_ITEMS; }
  });
  const [showQuickEditor, setShowQuickEditor] = useState(false);
  const [newQuickName, setNewQuickName] = useState('');
  const [newQuickPrice, setNewQuickPrice] = useState('');

  const saveQuickItems = (items) => {
    setQuickItems(items);
    localStorage.setItem('quick_items', JSON.stringify(items));
  };
  const addQuickItem = () => {
    const name = newQuickName.trim();
    const price = parseFloat(newQuickPrice);
    if (!name || !price || price <= 0) { toast.error('Name and price required'); return; }
    const item = {
      id: `q_${Date.now()}`, name, price,
      icon: 'fa-tag', color: 'bg-orange-500',
    };
    saveQuickItems([...quickItems, item]);
    setNewQuickName(''); setNewQuickPrice('');
    toast.success('Added to quick items');
  };
  const removeQuickItem = (id) => saveQuickItems(quickItems.filter((x) => x.id !== id));

  const addQuickToCart = (q) => {
    setCart((prev) => [...prev, { product_id: null, name: q.name, price: q.price, qty: 1 }]);
  };

  // Lock handlers
  const attemptLock = () => {
    setIsLocked(true);
    localStorage.setItem('register_locked', 'true');
    toast.success('Register locked');
  };
  const attemptUnlock = () => {
    if (unlockPin === '0300') {
      setIsLocked(false);
      localStorage.removeItem('register_locked');
      setShowUnlock(false);
      setUnlockPin('');
      toast.success('Unlocked');
    } else {
      toast.error('Wrong PIN');
      setUnlockPin('');
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await adminApi.get('/products');
      setProducts(res.data || []);
    } catch { toast.error('Could not load products'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Load sales history — backend auto-purges anything older than 24h, but we filter client-side too for safety
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await adminApi.get('/sales?limit=200');
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const recent = (res.data || []).filter((s) => {
        const t = s.created_at ? new Date(s.created_at).getTime() : 0;
        return t >= cutoff;
      });
      setSalesHistory(recent);
    } catch {
      toast.error('Could not load sales history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Refresh history whenever the history tab is opened or a new sale is made
  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  const clearHistory = async () => {
    if (!window.confirm('Clear all sales history? This cannot be undone.')) return;
    try {
      await adminApi.delete('/sales');
      setSalesHistory([]);
      toast.success('History cleared');
    } catch {
      toast.error('Could not clear history');
    }
  };

  // Reprint a receipt from history — reuses the existing receipt modal
  const reprintFromHistory = (sale) => {
    setReceiptSale({
      sale_number: sale.sale_number,
      subtotal: sale.subtotal,
      items: (sale.items || []).map((it) => ({
        name: it.name,
        price: it.price,
        qty: it.quantity || it.qty || 1,
      })),
      payment_method: sale.payment_method,
      date: sale.created_at ? new Date(sale.created_at) : new Date(),
    });
  };

  const categories = ['all', ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = products.filter((p) =>
    (category === 'all' || p.category === category) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  const addToCart = (product) => {
    if (product.stock <= 0) { toast.error('Out of stock'); return; }
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        if (existing.qty >= product.stock) { toast.error('No more in stock'); return prev; }
        return prev.map((i) => i.product_id === product.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { product_id: product.id, name: product.name, price: product.price, qty: 1, stock: product.stock }];
    });
  };

  // Add a manual line: custom amount (with optional name)
  const addCustomLine = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter an amount'); return; }
    const name = customName.trim() || 'Custom item';
    setCart((prev) => [...prev, { product_id: null, name, price: amt, qty: 1 }]);
    setAmount('');
    setCustomName('');
    toast.success(`Added ${name} — €${amt.toFixed(2)}`);
  };

  // Keypad helpers
  const keypadTap = (key) => {
    if (key === 'C') { setAmount(''); return; }
    if (key === '⌫') { setAmount((a) => a.slice(0, -1)); return; }
    if (key === '.') { if (!amount.includes('.')) setAmount((a) => (a || '0') + '.'); return; }
    // digit
    if (amount.includes('.') && amount.split('.')[1]?.length >= 2) return; // max 2 decimals
    setAmount((a) => (a === '0' && key !== '.') ? key : a + key);
  };
  const bumpAmount = (delta) => {
    const cur = parseFloat(amount) || 0;
    setAmount(Math.max(0, cur + delta).toFixed(2));
  };

  const updateQty = (idx, delta) => {
    setCart((prev) => prev.map((i, x) => x === idx ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  };
  const removeItem = (idx) => setCart((prev) => prev.filter((_, x) => x !== idx));
  const clearCart = () => { setCart([]); setLastSale(null); };

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const checkout = async (method) => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    setProcessing(true);
    try {
      // Only send items that have real product IDs to the sales endpoint (so stock decrements),
      // but keep custom lines in the receipt.
      const payload = {
        payment_method: method,
        items: cart
          .filter((i) => i.product_id)
          .map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.qty })),
      };
      let saleNumber;
      let subtotal = total;
      if (payload.items.length > 0) {
        const res = await adminApi.post('/sales', payload);
        saleNumber = res.data.sale_number;
      } else {
        // Custom-only sale — generate a client-side sale number (nothing to decrement)
        saleNumber = `SALE-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      }
      const sale = { sale_number: saleNumber, subtotal, items: cart, payment_method: method, date: new Date() };
      setLastSale(sale);
      setReceiptSale(sale);
      toast.success(`Sale complete · €${subtotal.toFixed(2)}`);
      setCart([]);
      load();
      // Silently refresh history so if the user opens the History tab it's fresh
      if (payload.items.length > 0) loadHistory();
    } catch { toast.error('Sale failed'); } finally { setProcessing(false); }
  };

  // Build printable receipt HTML in the chosen language and open a print window
  const printReceipt = (sale, lang) => {
    const t = T[lang];
    const dateStr = new Date(sale.date).toLocaleString(lang === 'nl' ? 'nl-NL' : 'en-GB');
    const rows = sale.items.map((i) => `
      <tr>
        <td style="padding:4px 0">${i.name}</td>
        <td style="text-align:center;padding:4px 0">${i.qty}</td>
        <td style="text-align:right;padding:4px 0;font-family:monospace">€${(i.price * i.qty).toFixed(2)}</td>
      </tr>
    `).join('');
    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${t.receipt} ${sale.sale_number}</title>
          <style>
            @page { size: 80mm auto; margin: 4mm; }
            * { box-sizing: border-box; }
            body { font-family: 'Courier New', Courier, monospace; padding: 8px; margin: 0; color: #000; width: 76mm; }
            h1 { font-size: 16px; margin: 0; text-align: center; letter-spacing: 1px; }
            .shop-meta { text-align: center; font-size: 11px; margin: 4px 0 8px; }
            .divider { border-top: 1px dashed #000; margin: 8px 0; }
            table { width: 100%; font-size: 11px; border-collapse: collapse; }
            th { text-align: left; border-bottom: 1px solid #000; padding: 4px 0; }
            .totals { font-size: 13px; }
            .totals .total-row { font-weight: bold; font-size: 15px; padding: 6px 0; border-top: 1px solid #000; }
            .footer { text-align: center; font-size: 11px; margin-top: 12px; }
            .footer .thanks { font-weight: bold; font-size: 13px; }
            .meta { font-size: 10px; margin: 6px 0; }
            .meta div { display: flex; justify-content: space-between; }
            @media screen {
              body { max-width: 320px; margin: 20px auto; box-shadow: 0 4px 24px rgba(0,0,0,0.15); padding: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>${SHOP.name.toUpperCase()}</h1>
          <div class="shop-meta">
            ${SHOP.address}<br/>
            ${SHOP.phone}
            ${SHOP.kvk ? `<br/>KvK: ${SHOP.kvk}` : ''}
            ${SHOP.btw ? `<br/>BTW: ${SHOP.btw}` : ''}
          </div>
          <div class="divider"></div>
          <div class="meta">
            <div><span>${t.date}:</span><span>${dateStr}</span></div>
            <div><span>${t.order}:</span><span>${sale.sale_number}</span></div>
          </div>
          <div class="divider"></div>
          <table>
            <thead>
              <tr>
                <th>${t.item}</th>
                <th style="text-align:center">${t.qty}</th>
                <th style="text-align:right">${t.price}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="divider"></div>
          <div class="totals">
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span>${t.subtotal}</span>
              <span style="font-family:monospace">€${sale.subtotal.toFixed(2)}</span>
            </div>
            <div class="total-row" style="display:flex;justify-content:space-between">
              <span>${t.total}</span>
              <span style="font-family:monospace">€${sale.subtotal.toFixed(2)}</span>
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:12px;margin-top:6px">
              <span>${t.payment}</span>
              <span>${sale.payment_method === 'cash' ? t.cash : t.card}</span>
            </div>
          </div>
          <div class="divider"></div>
          <div class="footer">
            <div class="thanks">${t.thanks}</div>
            <div>${t.comeAgain}</div>
          </div>
          <script>window.onload = function() { setTimeout(function() { window.print(); }, 200); };</script>
        </body>
      </html>
    `;
    const w = window.open('', '_blank', 'width=400,height=700');
    if (!w) { toast.error('Popup blocked — allow popups for this site'); return; }
    w.document.write(html);
    w.document.close();
  };

  const quickAmounts = [1, 2, 5, 10, 20, 50];
  const keys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '⌫'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="register-page">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {!isLocked ? (
            <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur rounded-xl font-semibold border border-white/10" data-testid="back-btn">
              <i className="fas fa-arrow-left"></i> Back to Admin
            </button>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500/20 text-red-300 rounded-xl font-semibold border border-red-500/30" data-testid="locked-indicator">
              <i className="fas fa-lock"></i> Register locked
            </div>
          )}
          <h1 className="text-2xl font-black flex items-center gap-2"><i className="fas fa-cash-register text-orange-400"></i> Register</h1>
          <div className="flex items-center gap-2">
            {!isLocked && (
              <button onClick={() => navigate('/inventory')} className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 rounded-xl font-semibold" data-testid="inventory-btn">
                <i className="fas fa-boxes-stacked mr-1"></i> Inventory
              </button>
            )}
            <button
              onClick={() => isLocked ? setShowUnlock(true) : attemptLock()}
              className={`px-4 py-2.5 rounded-xl font-semibold ${isLocked ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              data-testid="lock-toggle-btn"
            >
              <i className={`fas ${isLocked ? 'fa-lock' : 'fa-lock-open'} mr-1`}></i>
              {isLocked ? 'Unlock' : 'Lock Page'}
            </button>
          </div>
        </div>

        {/* Tab Switcher — Register / History / Warranty / Tax Form */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-1.5" data-testid="register-tabs">
          <button
            onClick={() => setActiveTab('register')}
            className={`py-2.5 rounded-xl font-bold text-sm transition-all ${
              activeTab === 'register'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
            data-testid="tab-register-btn"
          >
            <i className="fas fa-cash-register mr-2"></i> Register
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2.5 rounded-xl font-bold text-sm transition-all ${
              activeTab === 'history'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
            data-testid="tab-history-btn"
          >
            <i className="fas fa-clock-rotate-left mr-2"></i> History
          </button>
          <button
            onClick={() => setActiveTab('warranty')}
            className={`py-2.5 rounded-xl font-bold text-sm transition-all ${
              activeTab === 'warranty'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
            data-testid="tab-warranty-btn"
          >
            <i className="fas fa-shield-halved mr-2"></i> Warranty
          </button>
          <button
            onClick={() => setActiveTab('taxform')}
            className={`py-2.5 rounded-xl font-bold text-sm transition-all ${
              activeTab === 'taxform'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
            data-testid="tab-taxform-btn"
          >
            <i className="fas fa-file-invoice mr-2"></i> Tax Form
          </button>
        </div>

        {activeTab === 'register' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT — Keypad + Products */}
          <div className="lg:col-span-2 space-y-4">
            {/* Quick Sale Items */}
            <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-4" data-testid="quick-items-panel">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Quick sale</p>
                <button
                  onClick={() => setShowQuickEditor((v) => !v)}
                  className="text-xs px-2 py-1 bg-white/10 hover:bg-white/20 rounded-lg font-semibold"
                  data-testid="toggle-quick-editor-btn"
                >
                  <i className={`fas ${showQuickEditor ? 'fa-check' : 'fa-pencil'} mr-1`}></i>
                  {showQuickEditor ? 'Done' : 'Edit'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="quick-items-grid">
                {quickItems.map((q) => (
                  <div key={q.id} className="relative">
                    <button
                      onClick={() => addQuickToCart(q)}
                      className="w-full p-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-left transition-all active:scale-95"
                      data-testid={`quick-item-${q.id}`}
                    >
                      <div className={`w-8 h-8 ${q.color} rounded-lg flex items-center justify-center mb-1`}>
                        <i className={`fas ${q.icon} text-white text-sm`}></i>
                      </div>
                      <p className="font-bold text-xs leading-tight truncate">{q.name}</p>
                      <p className="text-orange-400 font-black text-sm mt-0.5">€{q.price.toFixed(2)}</p>
                    </button>
                    {showQuickEditor && (
                      <button
                        onClick={() => removeQuickItem(q.id)}
                        className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full text-xs shadow-lg"
                        data-testid={`remove-quick-${q.id}`}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {showQuickEditor && (
                <div className="mt-3 pt-3 border-t border-white/10 flex gap-2 flex-wrap" data-testid="add-quick-form">
                  <input
                    type="text"
                    value={newQuickName}
                    onChange={(e) => setNewQuickName(e.target.value)}
                    placeholder="Item name (e.g., Passport photo)"
                    className="flex-1 min-w-[140px] px-3 py-2 bg-white/10 border border-white/20 rounded-lg placeholder-white/40 focus:border-orange-400 focus:outline-none text-sm"
                    data-testid="new-quick-name"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={newQuickPrice}
                    onChange={(e) => setNewQuickPrice(e.target.value)}
                    placeholder="€"
                    className="w-24 px-3 py-2 bg-white/10 border border-white/20 rounded-lg placeholder-white/40 focus:border-orange-400 focus:outline-none text-sm"
                    data-testid="new-quick-price"
                  />
                  <button
                    onClick={addQuickItem}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg font-bold text-sm"
                    data-testid="add-quick-btn"
                  >
                    <i className="fas fa-plus mr-1"></i> Add
                  </button>
                </div>
              )}
            </div>

            {/* Keypad Panel */}
            <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-4" data-testid="keypad-panel">
              <div className="grid grid-cols-2 gap-3">
                {/* Amount display */}
                <div className="col-span-2 flex items-center gap-3">
                  <div className="flex-1 bg-black/40 rounded-xl px-4 py-3">
                    <p className="text-[10px] uppercase tracking-widest text-white/50">Amount</p>
                    <p className="text-4xl font-black text-orange-400 tabular-nums" data-testid="amount-display">
                      €{amount || '0.00'}
                    </p>
                  </div>
                  <button onClick={() => setAmount('')} className="px-3 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl font-bold" data-testid="clear-amount-btn">
                    <i className="fas fa-eraser mr-1"></i> Clear
                  </button>
                </div>

                {/* Optional custom item name */}
                <div className="col-span-2">
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="Item name (optional — e.g., Print A4, Lamination)"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg placeholder-white/40 focus:border-orange-400 focus:outline-none text-sm"
                    data-testid="custom-name-input"
                  />
                </div>

                {/* Keypad */}
                <div className="grid grid-cols-3 gap-2">
                  {keys.map((k) => (
                    <button
                      key={k}
                      onClick={() => keypadTap(k)}
                      className="py-4 bg-white/10 hover:bg-white/20 rounded-xl text-2xl font-black transition-all active:scale-95"
                      data-testid={`key-${k}`}
                    >
                      {k}
                    </button>
                  ))}
                </div>

                {/* Quick amounts + Add */}
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-3 gap-2">
                    {quickAmounts.map((q) => (
                      <button
                        key={q}
                        onClick={() => bumpAmount(q)}
                        className="py-3 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-xl text-sm font-bold"
                        data-testid={`quick-${q}`}
                      >
                        +€{q}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={addCustomLine}
                    disabled={!amount || parseFloat(amount) <= 0}
                    className="mt-auto py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl text-lg font-black disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="add-line-btn"
                  >
                    <i className="fas fa-plus mr-1"></i> Add to sale
                  </button>
                </div>
              </div>
            </div>

            {/* Products (still available) */}
            <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-4">
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2">Or tap a product</p>
              <div className="flex gap-2 flex-wrap mb-2">
                {categories.map((c) => (
                  <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1 rounded-lg text-xs font-bold ${category === c ? 'bg-orange-500 text-white' : 'bg-white/10 text-white/70'}`} data-testid={`cat-${c}`}>
                    {c === 'all' ? 'All' : c}
                  </button>
                ))}
              </div>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="w-full mb-3 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg placeholder-white/40 focus:border-orange-400 focus:outline-none text-sm" data-testid="search-input" />
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto" data-testid="pos-products">
                {filtered.length === 0 && <p className="col-span-full text-white/40 text-sm py-6 text-center">No products. Add in Inventory.</p>}
                {filtered.map((p) => (
                  <button key={p.id} onClick={() => addToCart(p)} disabled={p.stock <= 0} className={`bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg p-2 text-left transition-all ${p.stock <= 0 ? 'opacity-50' : ''}`} data-testid={`pos-product-${p.id}`}>
                    <p className="font-bold text-xs leading-tight truncate">{p.name}</p>
                    <p className="text-orange-400 font-black text-sm mt-0.5">€{p.price.toFixed(2)}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Cart & Checkout */}
          <div className="bg-white text-gray-800 rounded-2xl p-5 shadow-2xl sticky top-4 self-start" data-testid="cart-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-black text-lg flex items-center gap-2"><i className="fas fa-shopping-cart"></i> Sale</h2>
              {cart.length > 0 && <button onClick={clearCart} className="text-xs text-red-500 hover:underline">Clear all</button>}
            </div>
            {cart.length === 0 ? (
              <p className="text-gray-400 text-center py-8 text-sm">Tap keys or a product to start</p>
            ) : (
              <div className="space-y-2 mb-3 max-h-80 overflow-y-auto">
                {cart.map((i, idx) => (
                  <div key={idx} className="flex items-center gap-2 pb-2 border-b border-gray-100" data-testid={`cart-item-${idx}`}>
                    <div className="flex-1 min-w-0"><p className="font-bold text-sm truncate">{i.name}</p><p className="text-xs text-gray-400">€{i.price.toFixed(2)} each</p></div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQty(idx, -1)} className="w-6 h-6 bg-gray-100 rounded font-bold">−</button>
                      <span className="w-6 text-center font-bold text-sm">{i.qty}</span>
                      <button onClick={() => updateQty(idx, 1)} className="w-6 h-6 bg-gray-100 rounded font-bold">+</button>
                    </div>
                    <span className="font-black text-indigo-600 w-16 text-right text-sm">€{(i.price * i.qty).toFixed(2)}</span>
                    <button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-red-500"><i className="fas fa-times"></i></button>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t-2 border-gray-800 pt-3 mb-4">
              <div className="flex items-center justify-between text-2xl font-black"><span>Total</span><span data-testid="cart-total">€{total.toFixed(2)}</span></div>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">Mark as paid</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => checkout('cash')} disabled={cart.length === 0 || processing} className="py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pay-cash-btn">
                <i className="fas fa-money-bill"></i> Cash
              </button>
              <button onClick={() => checkout('card')} disabled={cart.length === 0 || processing} className="py-4 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pay-card-btn">
                <i className="fas fa-credit-card"></i> Card
              </button>
            </div>
            {lastSale && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="last-sale">
                <p className="text-xs text-emerald-700 font-bold uppercase tracking-wider">Last sale</p>
                <p className="font-mono font-bold text-emerald-800 text-sm">{lastSale.sale_number}</p>
                <p className="text-sm text-emerald-700 mb-2">€{lastSale.subtotal.toFixed(2)} · {lastSale.payment_method}</p>
                <button
                  onClick={() => setReceiptSale(lastSale)}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-sm"
                  data-testid="print-receipt-btn"
                >
                  <i className="fas fa-print mr-1"></i> Print Receipt
                </button>
              </div>
            )}
          </div>
        </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-5" data-testid="history-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-black flex items-center gap-2">
                  <i className="fas fa-clock-rotate-left text-orange-400"></i>
                  Sales History
                </h2>
                <p className="text-xs text-white/50 mt-0.5">
                  Auto-clears after 24 hours · {salesHistory.length} sale{salesHistory.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadHistory}
                  className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl font-semibold text-sm"
                  data-testid="refresh-history-btn"
                >
                  <i className={`fas fa-sync-alt ${historyLoading ? 'fa-spin' : ''} mr-1`}></i> Refresh
                </button>
                {salesHistory.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl font-semibold text-sm"
                    data-testid="clear-history-btn"
                  >
                    <i className="fas fa-trash mr-1"></i> Clear all
                  </button>
                )}
              </div>
            </div>

            {historyLoading ? (
              <div className="text-center py-12 text-white/50">
                <i className="fas fa-spinner fa-spin text-3xl mb-3"></i>
                <p>Loading sales...</p>
              </div>
            ) : salesHistory.length === 0 ? (
              <div className="text-center py-12 text-white/40" data-testid="history-empty">
                <i className="fas fa-receipt text-4xl mb-3"></i>
                <p className="font-semibold">No sales in the last 24 hours</p>
                <p className="text-xs mt-1">New sales appear here automatically</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {salesHistory.map((s) => {
                  const dt = s.created_at ? new Date(s.created_at) : new Date();
                  const timeStr = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                  const dateStr = dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                  const isCard = s.payment_method === 'card';
                  const itemCount = (s.items || []).reduce((a, it) => a + (it.quantity || it.qty || 1), 0);
                  return (
                    <div
                      key={s.id || s.sale_number}
                      className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3 transition-all"
                      data-testid={`history-row-${s.sale_number}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-mono font-bold text-sm text-orange-300">{s.sale_number}</span>
                            <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${isCard ? 'bg-blue-500/20 text-blue-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                              <i className={`fas ${isCard ? 'fa-credit-card' : 'fa-money-bill'} mr-1`}></i>
                              {s.payment_method}
                            </span>
                            <span className="text-[10px] text-white/40">
                              <i className="fas fa-clock mr-1"></i>{dateStr} · {timeStr}
                            </span>
                          </div>
                          <div className="text-xs text-white/60 truncate">
                            {itemCount} item{itemCount === 1 ? '' : 's'}: {(s.items || []).slice(0, 3).map((it) => it.name).join(', ')}
                            {(s.items || []).length > 3 && '…'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-lg font-black text-white tabular-nums">€{Number(s.subtotal || 0).toFixed(2)}</span>
                          <button
                            onClick={() => reprintFromHistory(s)}
                            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg"
                            title="Reprint receipt"
                            data-testid={`reprint-${s.sale_number}`}
                          >
                            <i className="fas fa-print"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'warranty' && (
          <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-4" data-testid="warranty-panel">
            <Warranty embedded={true} />
          </div>
        )}

        {activeTab === 'taxform' && (
          <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-4" data-testid="taxform-tab-panel">
            <TaxForm />
          </div>
        )}
      </div>

      {/* Unlock PIN modal */}
      {showUnlock && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setShowUnlock(false); setUnlockPin(''); }} data-testid="unlock-modal">
          <div className="bg-white text-gray-800 rounded-3xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-3">
                <i className="fas fa-lock text-2xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-black">Unlock Register</h3>
              <p className="text-sm text-gray-500 mt-1">Enter admin PIN to continue</p>
            </div>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={unlockPin}
              onChange={(e) => setUnlockPin(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') attemptUnlock(); }}
              placeholder="••••"
              autoFocus
              className="w-full text-center text-3xl font-black tracking-[0.5em] px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none mb-3"
              data-testid="unlock-pin-input"
            />
            <button
              onClick={attemptUnlock}
              disabled={unlockPin.length < 4}
              className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl disabled:opacity-50 text-lg"
              data-testid="confirm-unlock-btn"
            >
              <i className="fas fa-lock-open mr-2"></i> Unlock
            </button>
          </div>
        </div>
      )}

      {/* Receipt language + print modal */}
      {receiptSale && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReceiptSale(null)} data-testid="receipt-modal">
          <div className="bg-white text-gray-800 rounded-3xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-black flex items-center gap-2"><i className="fas fa-print text-emerald-600"></i> Print Receipt</h3>
              <button onClick={() => setReceiptSale(null)} className="w-8 h-8 rounded-full hover:bg-gray-100"><i className="fas fa-times"></i></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Choose the language:</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => setReceiptLang('en')}
                className={`p-5 rounded-2xl border-2 transition-all ${receiptLang === 'en' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid="lang-en-btn"
              >
                <p className="text-3xl mb-1">🇬🇧</p>
                <p className="font-black text-lg">English</p>
                <p className="text-xs text-gray-500 mt-1">Receipt · Item · Total</p>
              </button>
              <button
                onClick={() => setReceiptLang('nl')}
                className={`p-5 rounded-2xl border-2 transition-all ${receiptLang === 'nl' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid="lang-nl-btn"
              >
                <p className="text-3xl mb-1">🇳🇱</p>
                <p className="font-black text-lg">Nederlands</p>
                <p className="text-xs text-gray-500 mt-1">Bon · Artikel · Totaal</p>
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm">
              <p className="flex justify-between"><span className="text-gray-500">Order</span><span className="font-mono font-bold">{receiptSale.sale_number}</span></p>
              <p className="flex justify-between"><span className="text-gray-500">Items</span><span className="font-bold">{receiptSale.items.length}</span></p>
              <p className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-black text-emerald-600">€{receiptSale.subtotal.toFixed(2)}</span></p>
            </div>
            <button
              onClick={() => { printReceipt(receiptSale, receiptLang); setReceiptSale(null); }}
              className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-black rounded-xl hover:from-emerald-700 hover:to-teal-700 text-lg shadow-lg"
              data-testid="confirm-print-btn"
            >
              <i className="fas fa-print mr-2"></i> Print in {receiptLang === 'en' ? 'English' : 'Nederlands'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
